// DashboardFilters.js
import React, { useState } from 'react';
import LoadingWidget from '../../../components/LoadingWidget'; // Adjust path as needed

const DashboardFilters = ({
  activeTab,
  setActiveTab,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  modalType,
  setModalType,
  setShowAddModal,
  onRefresh,
  isFetching,
  API_BASE_URL
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'quotations', name: 'Quotations' },
    { id: 'users', name: 'Users' },
    { id: 'followups', name: 'Follow-ups' },
    { id: 'documents', name: 'Documents' },
    { id: 'subscribers', name: 'Newsletter Subscribers' },
    { id: 'newsletter', name: 'Send Newsletter' }
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setSearchTerm('');
    setFilters({
      status: '',
      service_type: '',
      date_from: '',
      date_to: '',
      role: '',
      account_status: '',
      followup_type: '',
      document_type: '',
    });
  };

  const handleExport = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('You must be logged in to export data.');
      return;
    }

    const params = new URLSearchParams({ export_format: 'csv' });
    if (filters.status)       params.append('status',       filters.status);
    if (filters.service_type) params.append('service_type', filters.service_type);
    if (filters.date_from)    params.append('date_from',    filters.date_from);
    if (filters.date_to)      params.append('date_to',      filters.date_to);

    const base = process.env.REACT_APP_API_URL;

    setIsExporting(true);
    fetch(`${base}/quotations/export/?${params.toString()}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error(`Export failed (${res.status})`);
        return res.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quotations_export_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => {
        alert(`Export failed: ${err.message}`);
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  return (
    <>
      {/* Loading overlay for refresh and export */}
      {(isFetching || isExporting) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-sm"></div>
          <div className="relative transform scale-150">
            <LoadingWidget />
          </div>
        </div>
      )}

    <div className="mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Dashboard
        </h1>

        <div className="flex items-center space-x-3">
          {/* Refresh button */}
          <button
            onClick={onRefresh}
            disabled={isFetching || isExporting}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh all data"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Export CSV button — only shown on the quotations tab */}
          {activeTab === 'quotations' && (
            <button
              onClick={handleExport}
              disabled={isFetching || isExporting}
              className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export quotations to CSV"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium">Export CSV</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Additional filters (quotations tab only) */}
      {activeTab === 'quotations' && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="submitted">Submitted</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted to Sale</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Type
              </label>
              <select
                value={filters.service_type}
                onChange={(e) => setFilters({ ...filters, service_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="solar">Solar Energy Only</option>
                <option value="electrical">Electrical Services Only</option>
                <option value="generator">Generator Services</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {(filters.status || filters.service_type || filters.date_from || filters.date_to) && (
            <div className="mt-4">
              <button
                onClick={() => setFilters({ status: '', service_type: '', date_from: '', date_to: '', role: '', account_status: '', followup_type: '', document_type: '' })}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Users filters */}
      {activeTab === 'users' && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                <option value="staff">Staff</option>
                <option value="customer">Customer</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Status
              </label>
              <select
                value={filters.account_status}
                onChange={(e) => setFilters({ ...filters, account_status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Unverified</option>
              </select>
            </div>
          </div>

          {(filters.role || filters.account_status) && (
            <div className="mt-4">
              <button
                onClick={() => setFilters({ ...filters, role: '', account_status: '' })}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Follow-ups filters */}
      {activeTab === 'followups' && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Follow-up Type
              </label>
              <select
                value={filters.followup_type}
                onChange={(e) => setFilters({ ...filters, followup_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="call">Phone Call</option>
                <option value="email">Email</option>
                <option value="site_visit">Site Visit</option>
                <option value="proposal_sent">Proposal Sent</option>
                <option value="negotiation">Negotiation</option>
                <option value="closed">Closed Deal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="no_response">No Response</option>
              </select>
            </div>
          </div>

          {(filters.followup_type || filters.status) && (
            <div className="mt-4">
              <button
                onClick={() => setFilters({ ...filters, followup_type: '', status: '' })}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Documents filters */}
      {activeTab === 'documents' && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Type
              </label>
              <select
                value={filters.document_type}
                onChange={(e) => setFilters({ ...filters, document_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="proposal">Proposal</option>
                <option value="contract">Contract</option>
                <option value="invoice">Invoice</option>
                <option value="site_photo">Site Photo</option>
                <option value="permit">Permit</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quotation ID
              </label>
              <input
                type="number"
                placeholder="e.g. 42"
                value={filters.quotation_id || ''}
                onChange={(e) => setFilters({ ...filters, quotation_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {(filters.document_type || filters.quotation_id) && (
            <div className="mt-4">
              <button
                onClick={() => setFilters({ ...filters, document_type: '', quotation_id: '' })}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default DashboardFilters;